import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Heart, HandHeart, Leaf, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '../components/ui/sheet';
import { Skeleton } from '../components/ui/skeleton';
import ProductCardV2 from '../components/ProductCardV2';
import { useSEO } from '../utils/seo';
import { readResponse } from '../utils/checkout';
import { withCharacterName, characterSlug, characterFilterName, pastelClass, CHARACTER_FILTERS } from '../lib/characters';
import { CUSTOM_ORDERS_ENABLED } from '../lib/shop';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import './ShopAtelier.css';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const SORTS = ['featured', 'popular', 'newest', 'price_asc', 'price_desc'];
const AVAILABILITY_OPTIONS = ['all', 'available', 'custom'];

const COPY = {
  fr: {
    seoTitle: 'Boutique — Le Placard à Doudous',
    seoDescription: 'Découvrez tous nos marque-pages en tissu faits main en France. Minkee ultra-doux, broderies artisanales, expédition soignée.',
    unavailable: 'La boutique est momentanément indisponible.',
    availability: { all: 'Tous', available: 'Disponibles', custom: 'Sur mesure' },
    sortLabel: 'Trier les produits', sortPlaceholder: 'Trier par',
    sorts: { featured: 'Mis en avant', popular: 'Popularité', newest: 'Nouveautés', price_asc: 'Prix croissant', price_desc: 'Prix décroissant' },
    sortCompanions: 'Trier les compagnons', collections: 'Collections', character: 'Personnage', soon: '· bientôt',
    price: (min, max) => `Prix : ${min} — ${max >= 50 ? '50+ €' : `${max} €`}`, minimum: 'Minimum (€)', maximum: 'Maximum (€)',
    reset: 'Réinitialiser les filtres', kicker: 'Toute la collection', title: ['Les compagnons ', 'de lecture'], intro: 'Cousus main par Margot, pour les pages qu’on aime.',
    searchPlaceholder: 'Rechercher un compagnon…', searchLabel: 'Rechercher un ami tout doux', availabilityLabel: 'Disponibilité des compagnons',
    filters: 'Filtres', filtersDescription: 'Affinez la boutique par personnage et prix.',
    loading: 'Chargement…', catalogUnavailable: 'Catalogue indisponible', count: n => `${n} compagnon${n > 1 ? 's' : ''}`,
    customLink: 'Composer le mien', seeAll: 'Tout voir', activeFilters: 'Filtres appliqués', removeFilter: name => `Retirer le filtre ${name}`,
    products: 'Produits', loadError: 'Impossible de charger la boutique. Réessayez dans un instant.', retry: 'Réessayer',
    emptyTitle: 'Aucun ami tout doux ne se cache ici…', emptyText: 'Essayez d’autres filtres — le placard a sûrement un petit trésor pour vous.', clear: 'Effacer les filtres',
    noteLabel: 'L’esprit de l’atelier', noteMessage: 'Des amis tout doux pour faire durer les belles histoires.',
    noteHandmade: ['Cousus main', 'en France'], noteSoft: 'Matières douces', noteCustom: ['À composer', 'sur mesure'], noteUnique: ['Pièces', 'uniques'],
  },
  en: {
    seoTitle: 'Shop — Le Placard à Doudous',
    seoDescription: 'Discover all our fabric bookmarks, handmade in France. Ultra-soft minky, hand embroidery, carefully packed.',
    unavailable: 'The shop is temporarily unavailable.',
    availability: { all: 'All', available: 'Available', custom: 'Made to order' },
    sortLabel: 'Sort products', sortPlaceholder: 'Sort by',
    sorts: { featured: 'Featured', popular: 'Most popular', newest: 'Newest', price_asc: 'Price: low to high', price_desc: 'Price: high to low' },
    sortCompanions: 'Sort the companions', collections: 'Collections', character: 'Character', soon: '· coming soon',
    price: (min, max) => `Price: €${min} — ${max >= 50 ? '€50+' : `€${max}`}`, minimum: 'Minimum (€)', maximum: 'Maximum (€)',
    reset: 'Reset filters', kicker: 'The whole collection', title: ['Reading ', 'companions'], intro: 'Sewn by hand by Margot, for the pages you love.',
    searchPlaceholder: 'Search for a companion…', searchLabel: 'Search for a cuddly friend', availabilityLabel: 'Companion availability',
    filters: 'Filters', filtersDescription: 'Narrow the shop down by character and price.',
    loading: 'Loading…', catalogUnavailable: 'Catalogue unavailable', count: n => `${n} companion${n > 1 ? 's' : ''}`,
    customLink: 'Design mine', seeAll: 'See all', activeFilters: 'Active filters', removeFilter: name => `Remove the ${name} filter`,
    products: 'Products', loadError: 'We couldn’t load the shop. Please try again in a moment.', retry: 'Try again',
    emptyTitle: 'No cuddly friend is hiding here…', emptyText: 'Try other filters — the cupboard surely has a small treasure for you.', clear: 'Clear filters',
    noteLabel: 'The spirit of the workshop', noteMessage: 'Cuddly friends to make good stories last.',
    noteHandmade: ['Sewn by hand', 'in France'], noteSoft: 'Soft fabrics', noteCustom: ['Made to order', 'just for you'], noteUnique: ['One-of-a-kind', 'pieces'],
  },
};

// Une seule collection pour l'instant : passer à true pour réafficher le filtre « Collections ».
const SHOW_COLLECTION_FILTER = false;

const AVAILABILITY = AVAILABILITY_OPTIONS.filter(value => CUSTOM_ORDERS_ENABLED || value !== 'custom');

export default function Shop() {
  const c = useCopy(COPY);
  const { locale } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rawCatalog, setCatalog] = useState([]);
  const catalog = useMemo(() => rawCatalog.map(product => withCharacterName(product, locale)), [rawCatalog, locale]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const search = searchParams.get('search') || '';
  const sort = SORTS.includes(searchParams.get('sort')) ? searchParams.get('sort') : 'featured';
  const availability = AVAILABILITY.includes(searchParams.get('availability')) ? searchParams.get('availability') : 'all';
  const selectedCollections = searchParams.getAll('collection');
  const selectedCharacters = searchParams.getAll('personnage').filter(slug => CHARACTER_FILTERS.some(filter => filter.slug === slug));
  const minPrice = Math.min(50, Math.max(0, Number(searchParams.get('min_price')) || 0));
  const parsedMax = searchParams.get('max_price') == null ? 50 : Number(searchParams.get('max_price'));
  const maxPrice = Math.max(minPrice, Math.min(50, Number.isFinite(parsedMax) ? parsedMax : 50));
  const priceRange = [minPrice, maxPrice];
  const [filtersOpen, setFiltersOpen] = useState(false);
  const collections = useMemo(() => [...new Set(catalog.map(p => p.collection).filter(Boolean))].sort(), [catalog]);
  const characterCounts = useMemo(() => Object.fromEntries(CHARACTER_FILTERS.map(filter => [filter.slug, catalog.filter(p => characterSlug(p.slug) === filter.slug).length])), [catalog]);
  const products = catalog.filter(p => (
    (!selectedCollections.length || selectedCollections.includes(p.collection))
    && (!selectedCharacters.length || selectedCharacters.includes(characterSlug(p.slug)))
    && (availability !== 'available' || (!p.made_to_order && p.status !== 'adopted' && p.stock > 0))
    && (availability !== 'custom' || p.custom_available !== false)
    && p.price >= minPrice && (maxPrice >= 50 || p.price <= maxPrice)
    && (!search.trim() || [p.name, p.description, p.collection].some(value => String(value || '').toLocaleLowerCase(locale).includes(search.trim().toLocaleLowerCase(locale))))
  ));
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`${API}/api/products?sort=${sort}`, { signal: controller.signal })
      .then(res => readResponse(res, c.unavailable))
      .then(data => {
        if (!Array.isArray(data)) throw new Error(c.unavailable);
        if (!controller.signal.aborted) setCatalog(data);
      })
      .catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, retry]);

  const updateParam = (key, values) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.delete(key);
    for (const value of values) if (value !== '') next.append(key, value);
    return next;
  }, { replace: true });
  const toggleCollection = value => updateParam('collection', selectedCollections.includes(value) ? selectedCollections.filter(x => x !== value) : [...selectedCollections, value]);
  const toggleCharacter = value => updateParam('personnage', selectedCharacters.includes(value) ? selectedCharacters.filter(x => x !== value) : [...selectedCharacters, value]);
  const characterLabel = slug => { const filter = CHARACTER_FILTERS.find(item => item.slug === slug); return filter ? characterFilterName(filter, locale) : slug; };
  const clearFilters = () => setSearchParams(sort === 'featured' ? {} : { sort }, { replace: true });
  const hasFilters = Boolean(search || availability !== 'all' || selectedCollections.length || selectedCharacters.length || minPrice > 0 || maxPrice < 50);
  const filterCount = selectedCollections.length + selectedCharacters.length + Number(minPrice > 0 || maxPrice < 50) + Number(Boolean(search)) + Number(availability !== 'all');

  const renderSort = () => (
    <Select value={sort} onValueChange={value => updateParam('sort', [value])}>
      <SelectTrigger aria-label={c.sortLabel} className="shop-atelier-sort" data-testid="shop-sort-select">
        <SelectValue placeholder={c.sortPlaceholder} />
      </SelectTrigger>
      <SelectContent>
        {SORTS.map(value => <SelectItem key={value} value={value}>{c.sorts[value]}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const renderFilters = () => (
    <div className="shop-atelier-filter-options space-y-6">
      <div className="shop-atelier-sheet-sort">
        <p className="font-sans text-sm font-semibold mb-3">{c.sortCompanions}</p>
        {renderSort()}
      </div>
      {SHOW_COLLECTION_FILTER && <div>
        <p className="font-sans text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">{c.collections}</p>
        <div className="space-y-3">
          {collections.map(name => (
            <div key={name} className="flex items-center gap-3">
              <Checkbox id={`col-${name}`} checked={selectedCollections.includes(name)} onCheckedChange={() => toggleCollection(name)} />
              <label htmlFor={`col-${name}`} className="font-sans text-sm text-[hsl(var(--foreground))] cursor-pointer">{name}</label>
            </div>
          ))}
        </div>
      </div>}
      <div>
        <p className="font-sans text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">{c.character}</p>
        <div className="space-y-3">
          {CHARACTER_FILTERS.map(filter => (
            <div key={filter.slug} className="flex items-center gap-3">
              <Checkbox id={`char-${filter.slug}`} checked={selectedCharacters.includes(filter.slug)} onCheckedChange={() => toggleCharacter(filter.slug)} />
              <label htmlFor={`char-${filter.slug}`} className="font-sans text-sm text-[hsl(var(--foreground))] cursor-pointer">
                <span className={`pastel-dot ${pastelClass(filter.slug)} mr-2`} aria-hidden="true" />
                {characterFilterName(filter, locale)}{!loading && !characterCounts[filter.slug] && <span className="ml-1.5 text-xs text-[hsl(var(--muted-foreground))]">{c.soon}</span>}
              </label>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="font-sans text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4">
          {c.price(priceRange[0], priceRange[1])}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="font-sans text-sm">{c.minimum}
            <Input type="number" min={0} max={maxPrice} value={minPrice} onChange={e => updateParam('min_price', [String(Math.min(maxPrice, Math.max(0, Number(e.target.value))))])} />
          </label>
          <label className="font-sans text-sm">{c.maximum}
            <Input type="number" min={minPrice} max={50} value={maxPrice} onChange={e => updateParam('max_price', [String(Math.max(minPrice, Math.min(50, Number(e.target.value))))])} />
          </label>
        </div>
      </div>
      {hasFilters && (
        <Button variant="outline" size="sm" onClick={clearFilters} className="rounded-xl font-sans text-xs w-full">
          <X className="h-3 w-3 mr-2" /> {c.reset}
        </Button>
      )}
    </div>
  );

  return (
    <div className="atelier-page shop-atelier">
      <div className="atelier-container shop-atelier-container">
        <header className="shop-atelier-header">
          <div className="shop-atelier-intro">
            <span className="kicker">{c.kicker}</span>
            <h1>{c.title[0]}<i>{c.title[1]}</i></h1>
            <p>{c.intro}</p>
          </div>
          <div className="shop-atelier-toolbar">
          <div className="shop-atelier-search">
            <Search aria-hidden="true" size={19} />
            <Input
              placeholder={c.searchPlaceholder}
              aria-label={c.searchLabel}
              value={search}
              onChange={e => updateParam('search', [e.target.value])}
              className="shop-atelier-search-input"
              data-testid="shop-search-input"
            />
          </div>
          <div className="shop-atelier-controls">
            <div className="shop-atelier-availability" role="group" aria-label={c.availabilityLabel}>
              {AVAILABILITY.map(value => <button key={value} type="button"
                aria-pressed={availability === value}
                onClick={() => updateParam('availability', value === 'all' ? [] : [value])}>
                {c.availability[value]}
              </button>)}
            </div>
          <div className="shop-atelier-tools">
            <div className="shop-atelier-desktop-sort">{renderSort()}</div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="shop-atelier-filter" data-testid="shop-filters-open-button">
                  <SlidersHorizontal className="h-4 w-4 mr-2" /> {c.filters}
                  {hasFilters && <Badge className="ml-1.5 rounded-full px-1.5 py-0 text-xs bg-primary text-primary-foreground border-0">{filterCount}</Badge>}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="shop-atelier-sheet overflow-y-auto" data-lenis-prevent>
                <SheetHeader><SheetTitle className="font-serif text-xl">{c.filters}</SheetTitle><SheetDescription className="sr-only">{c.filtersDescription}</SheetDescription></SheetHeader>
                <div className="mt-6">{renderFilters()}</div>
              </SheetContent>
            </Sheet>
          </div>
          </div>
          </div>
        </header>
        <div className="shop-atelier-status" role="status" aria-live="polite">
          <span>{loading ? c.loading : error ? c.catalogUnavailable : c.count(products.length)}</span>
          <div className="shop-atelier-status-actions">
            {CUSTOM_ORDERS_ENABLED && <Link to="/sur-mesure">{c.customLink}</Link>}
            {hasFilters && <button
              type="button"
              onClick={clearFilters}
              className="shop-atelier-clear"
              data-testid="shop-clear-filters-link"
            >
              {c.seeAll}
            </button>}
          </div>
        </div>

        {/* Active filters */}
        {(selectedCollections.length > 0 || selectedCharacters.length > 0) && (
          <div className="shop-atelier-active-filters" aria-label={c.activeFilters}>
            {selectedCollections.map(name => (
              <button type="button" aria-label={c.removeFilter(name)} key={name} className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 font-sans text-xs font-medium text-foreground hover:bg-secondary" onClick={() => toggleCollection(name)}>
                {name} <X className="h-3 w-3 ml-1" />
              </button>
            ))}
            {selectedCharacters.map(slug => (
              <button type="button" aria-label={c.removeFilter(characterLabel(slug))} key={slug} className={`pastel-chip ${pastelClass(slug)} border border-transparent px-3 py-1 hover:border-current`} onClick={() => toggleCharacter(slug)}>
                {characterLabel(slug)} <X className="h-3 w-3 ml-1" />
              </button>
            ))}
          </div>
        )}

        <div>
          <section aria-label={c.products} aria-busy={loading}>
            {loading ? (
              <div className="shop-atelier-grid">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="shop-atelier-skeleton" />)}
              </div>
            ) : error ? (
              <div className="py-16 text-center" role="alert">
                <p className="mb-4 font-sans text-muted-foreground">{c.loadError}</p>
                <Button variant="outline" onClick={() => setRetry(value => value + 1)} className="rounded-full">{c.retry}</Button>
              </div>
            ) : products.length === 0 ? (
              <div className="py-24 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
                  <Search className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <p className="font-serif text-2xl text-foreground mb-2">{c.emptyTitle}</p>
                <p className="mx-auto mb-7 max-w-xs font-sans text-sm leading-relaxed text-muted-foreground">{c.emptyText}</p>
                <Button variant="outline" onClick={clearFilters} className="rounded-full border-input bg-card px-6 font-sans hover:bg-secondary">{c.clear}</Button>
              </div>
            ) : (
              <div className="shop-atelier-grid">
                {products.map(p => <ProductCardV2 key={p.id} product={p} catalogue />)}
                {!hasFilters && products.length === 5 && <aside className="shop-atelier-note" aria-label={c.noteLabel}>
                  <div className="shop-atelier-note-message"><Leaf aria-hidden="true" /><p>{c.noteMessage}</p><Heart aria-hidden="true" /></div>
                  <div className="shop-atelier-note-details">
                    <p><HandHeart aria-hidden="true" />{c.noteHandmade[0]}<br />{c.noteHandmade[1]}</p>
                    <p><Leaf aria-hidden="true" />{c.noteSoft}</p>
                    {CUSTOM_ORDERS_ENABLED
                      ? <Link to="/sur-mesure"><Heart aria-hidden="true" />{c.noteCustom[0]}<br />{c.noteCustom[1]}</Link>
                      : <p><Heart aria-hidden="true" />{c.noteUnique[0]}<br />{c.noteUnique[1]}</p>}
                  </div>
                </aside>}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
